'use strict';

import React, { Component } from 'react';
import { CircularProgress, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Mic, Stop, AttachFile } from '@material-ui/icons';

/**
 * AudioCapture — capture ou import d'un fichier audio, transcription via BFF /audio/transcribe.
 *
 * Props :
 *   boxid        {string}   Paygo ID transmis au BFF (header Paygo_ID vers domainModel)
 *   onTranscription {Function} Callback appelé avec :
 *       { audioId, transcript, summary, segments, speakerCount, language, error }
 *       Règle d'usage : speakerCount <= 1 → utiliser transcript ; sinon → utiliser summary
 *
 * Deux modes (champ multipart `purpose`) :
 *   - Micro (MediaRecorder, HTTPS requis) = prise de note dictée → purpose=note :
 *     transcription seule, l'audio est SUPPRIMÉ côté serveur (pas d'audioId renvoyé)
 *   - Import de fichier (<input accept="audio/*">) = enregistrement de conversation
 *     → purpose=conversation : audio conservé et rattachable à la panne
 *
 * L'audioId est généré (crypto.randomUUID) une fois par capture / fichier sélectionné
 * et conservé en state pour permettre un retry réseau idempotent (même audioId = pas de
 * double facturation Mistral côté domainModel).
 *
 * Validation côté client : taille ≤ 25 Mo, type audio/*.
 * En cas d'erreur transcription (champ `error` présent dans la réponse), un avertissement
 * non bloquant est affiché et l'audio reste référencé (onTranscription appelé normalement).
 */

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 Mo

function generateAudioId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

class AudioCapture extends Component {
    state = {
        status: 'idle',       // 'idle' | 'recording' | 'processing'
        audioId: null,
        errorMsg: null,
        mediaRecorder: null,
        audioChunks: [],
    }

    _startRecording = async () => {
        this.setState({ errorMsg: null });
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.setState({ errorMsg: 'Enregistrement audio non disponible (HTTPS requis).' });
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
            const options = mimeType ? { mimeType } : {};
            const mediaRecorder = new MediaRecorder(stream, options);
            const audioChunks = [];
            const audioId = generateAudioId();

            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) audioChunks.push(e.data);
            };

            mediaRecorder.onstop = () => {
                stream.getTracks().forEach(function(t) { t.stop(); });
                const blob = new Blob(audioChunks, { type: mimeType || 'audio/webm' });
                // Micro = prise de note dictée : transcription seule, audio non conservé côté serveur
                this._sendAudio(blob, audioId, 'enregistrement.webm', 'note');
            };

            mediaRecorder.start();
            this.setState({ status: 'recording', mediaRecorder, audioChunks, audioId });
        } catch (err) {
            this.setState({ errorMsg: 'Accès au microphone refusé : ' + err.message });
        }
    }

    _stopRecording = () => {
        const { mediaRecorder } = this.state;
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            this.setState({ status: 'processing' });
            mediaRecorder.stop();
        }
    }

    _handleFileChange = (e) => {
        var file = e.target.files && e.target.files[0];
        // Réinitialise la valeur pour permettre de sélectionner le même fichier
        e.target.value = '';
        if (!file) return;

        this.setState({ errorMsg: null });

        if (!file.type.startsWith('audio/')) {
            this.setState({ errorMsg: 'Format non supporté. Veuillez joindre un fichier audio (mp3, wav, webm, ogg, m4a…).' });
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            this.setState({ errorMsg: 'Fichier trop volumineux (maximum 25 Mo).' });
            return;
        }

        const audioId = generateAudioId();
        this.setState({ audioId, status: 'processing' }, () => {
            // Fichier joint = enregistrement de conversation (appel client) : audio conservé et rattaché
            this._sendAudio(file, audioId, file.name, 'conversation');
        });
    }

    _sendAudio = async (fileOrBlob, audioId, filename, purpose) => {
        const { boxid } = this.props;

        const formData = new FormData();
        formData.append('file', fileOrBlob, filename);
        formData.append('audioId', audioId);
        formData.append('purpose', purpose || 'conversation');
        if (boxid) formData.append('boxid', boxid);

        try {
            const response = await fetch('/audio/transcribe', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            const data = await response.json();

            this.setState({ status: 'idle' });

            if (this.props.onTranscription) {
                this.props.onTranscription({
                    // audioId serveur uniquement : sans confirmation de domainModel
                    // (échec BFF/réseau), référencer l'audioId local créerait un lien mort.
                    audioId: data.audioId || null,
                    transcript: data.transcript || '',
                    summary: data.summary || null,
                    segments: data.segments || [],
                    speakerCount: typeof data.speakerCount === 'number' ? data.speakerCount : 0,
                    language: data.language || '',
                    error: data.error || null
                });
            }

            if (data.error) {
                this.setState({
                    errorMsg: data.audioId
                        ? 'Transcription incomplète (' + data.error + '). L\'audio reste référencé, saisie manuelle possible.'
                        : 'Échec de l\'envoi audio (' + data.error + '). L\'audio n\'a pas été enregistré — réessayez.'
                });
            }
        } catch (err) {
            this.setState({
                status: 'idle',
                errorMsg: 'Erreur réseau. Vous pouvez réessayer (le même fichier sera envoyé sans double facturation).'
            });
        }
    }

    render() {
        const { status, errorMsg } = this.state;

        return (
            <div>
                {status === 'processing' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="textSecondary">Transcription en cours…</Typography>
                    </div>
                ) : status === 'recording' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <Tooltip title="Arrêter l'enregistrement">
                            <IconButton size="small" onClick={this._stopRecording} style={{ color: '#c62828' }}>
                                <Stop fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Typography variant="caption" style={{ color: '#c62828' }}>Enregistrement…</Typography>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2 }}>
                        <Tooltip title="Dicter une note (transcription seule, audio non conservé)">
                            <IconButton size="small" onClick={this._startRecording}>
                                <Mic fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Joindre un enregistrement d'appel (audio conservé avec la panne)">
                            <IconButton size="small" component="label">
                                <AttachFile fontSize="small" />
                                <input
                                    type="file"
                                    accept="audio/*"
                                    style={{ display: 'none' }}
                                    onChange={this._handleFileChange}
                                />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
                {errorMsg && (
                    <Typography
                        variant="body2"
                        style={{ color: '#c62828', marginTop: 4, fontSize: '0.82rem' }}
                    >
                        {errorMsg}
                    </Typography>
                )}
            </div>
        );
    }
}

export default AudioCapture;
